import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../shared/api/client';

// Set while the server reports an active 7-day preview. PreviewBanner keys its
// one-time post-expiry conversion variant off this flag — the server can't
// know "was previously on preview" once the window lapses.
export const PREVIEW_SEEN_KEY = 'babytrack-was-preview';

// Complimentary Premium rows: 'comp' is fleet canon, 'complimentary' is the
// legacy support@ demo row. Both mean "nothing to buy, nothing to manage".
const COMP_PROVIDERS = ['comp', 'complimentary'];

export function useSubscription() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => apiGet('/subscription/status'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Legacy receipts still store plan 'plus'; it resolves to the same limits as
  // premium and is never shown as "Plus" again. The server already normalises —
  // this guards the optimistic IAP cache patch (which writes the stored plan
  // name) and any mixed-deploy window.
  const rawPlan = data?.plan || 'free';
  const plan = rawPlan === 'plus' ? 'premium' : rawPlan;
  const status = data?.status || 'active';
  const provider = data?.provider || null;
  const limits = data?.limits || {};
  const trialEnd = data?.trialEnd || null;
  const interval = data?.interval || 'month';
  const isTrial = data?.isTrial || false;
  const inAppNudge = data?.inAppNudge || null;
  const isPreview = data?.isPreview || false;
  const previewEndsAt = data?.previewEndsAt || null;

  // v1.5.0 status fields (docs/v150-spec.md §2.3). Each reads safely from a
  // server that does not send it yet.
  // A 30-day referral pass: Premium without a subscription — like the preview,
  // the user can still buy.
  const isPass = data?.isPass === true;
  const passEndsAt = data?.passEndsAt || null;
  // { kind: 'trial'|'subscription'|'pass'|'preview', endedAt } — only on Free.
  const lapsed = data?.lapsed || null;
  // false once the account has Apple purchase history: trial wording is hidden
  // (Apple still decides real eligibility at purchase). Absent → may be eligible
  // (an older server); no status at all is hasStatus's job, below.
  const trialEligible = data?.trialEligible !== false;
  // { redeemedCount, lastRedeemedAt } once a friend has used this user's pass.
  const referral = data?.referral || null;
  // v1.5.1 (docs/v151-spec.md §1.2, §2.4): { kind: 'day8', since } while a
  // Free account that never subscribed is due its one-off second ask
  // (useSecondAsk) — null otherwise, and from a 1.5.0 server that never sends it.
  const secondAsk = data?.secondAsk || null;
  // v1.5.1 (docs/v151-spec.md §6.2): the server has answered at least once.
  // True after any successful load, and still true while a background refetch
  // fails (the last good status stays cached); false while the first load runs,
  // after a failed first load and while it waits offline — paused, a query
  // with no data reports isLoading false, so isLoading alone cannot tell.
  // Anything that claims something about the account (the trial wording)
  // waits for it.
  const hasStatus = data !== undefined;
  const isComp = COMP_PROVIDERS.includes(provider);
  // A paid plan this account bought through Apple: the one case with a
  // subscription to manage and a price to keep.
  const ownsAppleSubscription = plan !== 'free' && !isPreview && !isPass && !isComp;

  // Remember that this device saw the preview so the post-expiry conversion
  // banner can fire exactly once after the drop to free. Converting to a paid
  // plan clears the flag — a later lapse to free is a churned subscriber, not
  // a preview expiry, and must not get first-week copy.
  const isPaidPlan = plan !== 'free';
  useEffect(() => {
    try {
      if (isPreview) {
        localStorage.setItem(PREVIEW_SEEN_KEY, '1');
      } else if (isPaidPlan) {
        localStorage.removeItem(PREVIEW_SEEN_KEY);
      }
    } catch {
      /* localStorage unavailable — the conversion banner just won't show */
    }
  }, [isPreview, isPaidPlan]);

  function hasFeature(feature) {
    return limits.features?.includes(feature) || false;
  }

  return {
    plan,
    status,
    provider,
    limits,
    currentPeriodEnd: data?.currentPeriodEnd,
    trialEnd,
    interval,
    isTrial,
    inAppNudge,
    isPreview,
    previewEndsAt,
    isPass,
    passEndsAt,
    lapsed,
    trialEligible,
    referral,
    secondAsk,
    isComp,
    ownsAppleSubscription,
    isLoading,
    hasStatus,
    error,
    refetch,
    hasFeature,
    isPaid: plan !== 'free',
  };
}
